import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, Nav } from "react-bootstrap";
import AdPlanPanel from "../../../components/core/blocks/AdPlan/AdPlanPanel";
import AdPlanNotificationMenu from "../../../components/core/blocks/AdPlan/AdPlanNotificationMenu";
import ScheduledBudgetSummary from "./ScheduledBudgetSummary";
import Seasonality from "./Seasonality";
import AdPlanServingHoursMenu from "../../../components/core/blocks/AdPlan/AdPlanServingHoursMenu";
import { DayPartingChart } from "../dayparting/components/DayPartingChart";
import ChangeLog from "./ChangeLog";
import RulesSection from "./RulesSection";
import AdvertisementHarvestingConfiguration from "../../../components/core/blocks/AdPlan/AdvertisementHarvestingConfiguration";
import { useFetch } from "../../../hooks/api";
import AmazonDebug from "../../../components/custom/AmazonDebug";
import HarvestingRules from "./HavestingRules";
import { AuditLog } from "../../audit_logs/AuditLog";
import { useSimpleCan } from "../../../config/casl/casl";

const SettingsSection = ({
  sections,
  categoryId,
  marketPlace,
  adPlanData,
  adPlanParams,
  channel,
  user,
  categoryDetails,
  preferences,
  isAdArchived = false,
}) => {
  const { t } = useTranslation(["advertising"]);
  const [settingsSectionTab, setSettingsSectionTab] = useState("mediaPlan");
  // Archived ads are read-only across every configuration form.
  const userCanUpdateAds = useSimpleCan("update", "Ad") && !isAdArchived;

  const { data: adHarvestingConfigData } = useFetch(
    ["adHarvestingConfig", adPlanData?.id],
    "/api/harvesting_relationships",
    {
      dest_advertisement: adPlanData?.id,
    },
    {
      enabled: !!adPlanData?.id,
      select: (res) => res.data?.results ?? [],
    },
  );

  const renderSettingsContent = (tab) => {
    switch (tab) {
      case "mediaPlan": {
        return (
          <Row>
            <Col xs={12} lg={11}>
              <AdPlanPanel
                adId={adPlanData?.id}
                categoryId={adPlanData?.category}
                categories={categoryDetails ? [categoryDetails] : []}
                roas={preferences.acos_or_roas === "roas"}
                isAdArchived={isAdArchived}
              />
            </Col>
          </Row>
        );
      }

      case "seasonality":
        return (
          <Seasonality
            marketPlace={marketPlace.marketPlace}
            adPlanData={adPlanData}
            adPlanParams={adPlanParams}
            categoryId={categoryId}
          />
        );
      case "scheduledChanges":
        return (
          <ScheduledBudgetSummary
            state={adPlanParams}
            adId={adPlanParams?.id}
            marketPlace={marketPlace}
            channel={channel}
            jumpToTab={(value) => setSettingsSectionTab(value)}
          />
        );
      case "servingHours":
        return (
          <AdPlanServingHoursMenu
            adId={adPlanData?.id}
            marketPlace={marketPlace}
            organizationType={user.organization_type}
            adPlanData={adPlanData}
          />
        );
      case "day_parting":
        return <DayPartingChart advertisement={adPlanData} categoryId={adPlanData?.category} />;
      case "notifications":
        return (
          <AdPlanNotificationMenu
            adId={adPlanData?.id}
            categoryId={adPlanData?.category}
            roas={preferences.acos_or_roas === "roas"}
            readOnly={!userCanUpdateAds}
          />
        );
      case "changeLog":
        return <ChangeLog annotationEntityId={parseInt(adPlanParams?.id)} annotationEntityType={"advertisement"} />;
      case "rules":
        return <RulesSection advertisement={adPlanData} readOnly={!userCanUpdateAds} />;
      case "harvesting":
        return (
          <AdvertisementHarvestingConfiguration
            adHarvestingConfigData={adHarvestingConfigData?.[0]}
            advertisement={adPlanData}
            adPlanParams={adPlanParams}
            isAdArchived={isAdArchived}
          />
        );
      case "harvestingRules":
        return <HarvestingRules advertisement={adPlanData} />;
      case "debug": {
        const campaigns = adPlanParams?.campaignIds ?? {};
        const adType = adPlanData?.ad_type;
        const entity =
          {
            10: "sp_campaign",
            14: "sp_campaign",
            19: "sb_campaign",
            11: "sb_campaign",
            12: "sbv_campaign",
            22: "sd_campaign",
          }?.[adType] ?? "sp_campaign";
        const entities = Object.values(campaigns).map((campaign) => ({ entity, entityId: campaign }));
        return <AmazonDebug entities={entities} />;
      }
      case "auditLog": {
        return <AuditLog entity="campaign" ids={Object.values(adPlanParams?.campaignIds ?? {})} />;
      }
      default:
        return null;
    }
  };

  return (
    <Row className="fs-standard">
      <Col xs={12} md={2} style={{ borderRight: "1px solid #ccc" }}>
        <div className="pt-2"></div>
        <Nav
          variant="underline"
          onSelect={(tabId) => {
            return setSettingsSectionTab(tabId);
          }}
          activeKey={settingsSectionTab}
          className="flex-column text-xs-center text-md-start"
        >
          {sections?.map((section) => {
            const { key, label } = section;
            return (
              <Nav.Link eventKey={key} key={key} className="text-xs-center text-md-start py-2">
                {typeof label === "function" ? t(label) : label}
              </Nav.Link>
            );
          })}
        </Nav>
      </Col>
      <Col xs={12} md={10} className="p-3">
        {renderSettingsContent(settingsSectionTab)}
      </Col>
    </Row>
  );
};

export default SettingsSection;
